import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Icon, Link as MuiLink, TextBody, } from '@ally/metronome-ui';
import { muiErrorFill } from '@ally/metronome-icons';
import { Private } from '@ally/private';
import { IconWrapper, NeedsAttentionContainer, OL, OLLI, StyledDD, StyledDL, StyledDT, } from './SharedStyles';
import { formattedDate, getDebitCardAccountDisplay } from '../utils';
export const DebitOrderedDescription = () => {
    return (React.createElement(TextBody, { tag: "p", size: "sm" }, "We generally ship out debit cards 1-2 business days after your order. Check back later for your tracking info."));
};
export const DebitNeedsAttentionDescription = () => {
    return (React.createElement(React.Fragment, null,
        React.createElement(NeedsAttentionContainer, { tag: "div", size: "sm" },
            React.createElement(IconWrapper, { "aria-label": "Needs Attention", role: "img" },
                React.createElement(Icon, { icon: muiErrorFill, fill: "warning", size: "md", "aria-hidden": true })),
            React.createElement(TextBody, { tag: "p", size: "sm" }, "Add money to your account, and we\u2019ll ship your card. Give us a day or two if you\u2019ve already started a transfer.")),
        React.createElement(Box, { mt: "md", ml: "lg" },
            React.createElement(TextBody, { tag: "p", size: "sm" }, "Here are four easy ways to add money:"),
            React.createElement(OL, null,
                React.createElement(OLLI, null,
                    React.createElement(MuiLink, { text: "Make a transfer" },
                        React.createElement(Link, { to: "/payments/transfers" }))),
                React.createElement(OLLI, null,
                    React.createElement(MuiLink, { text: "Deposit a check" },
                        React.createElement(Link, { to: "/payments/deposit-checks" }))),
                React.createElement(OLLI, null,
                    React.createElement(MuiLink, { text: "Wire money" },
                        React.createElement(Link, { to: "/payments/send-wire" }))),
                React.createElement(OLLI, null,
                    React.createElement(MuiLink, { unsupportedContent: React.createElement(React.Fragment, null,
                            "Send money with Zelle",
                            React.createElement("sup", null, "\u00AE")), allytmln: "Send money with Zelle" },
                        React.createElement(Link, { to: "/payments/pay-people" })))))));
};
export const DebitShippedDescription = ({ eventModelData, setConfirmModal, confirmationTriggerRef }) => {
    const { estimatedDeliveryDate, trackingUrl } = eventModelData;
    return (React.createElement(React.Fragment, null,
        React.createElement(TextBody, { tag: "p", size: "sm" },
            "Your card is on the way!",
            ' ',
            estimatedDeliveryDate &&
                `You should get it by ${formattedDate(estimatedDeliveryDate, 'MMM D, YYYY')}.`),
        trackingUrl && (React.createElement(Box, { mt: "md" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                React.createElement(Button, { variant: "link", text: "Track your card", onClick: () => setConfirmModal({
                        showConfirmModal: true,
                        externalLink: trackingUrl,
                    }), ref: confirmationTriggerRef }))))));
};
export const DebitDeliveredDescription = ({ eventModelData, setConfirmModal, confirmationTriggerRef }) => {
    const { trackingUrl } = eventModelData;
    return (React.createElement(React.Fragment, null,
        React.createElement(TextBody, { tag: "p", size: "sm" },
            "Special delivery. \uD83D\uDCEB Your card\u2019s in the mailbox. Just call",
            ' ',
            React.createElement(MuiLink, { text: "1-833-414-4855", allytmln: "1-833-414-4855", "aria-label": "1, 8 3 3, 4 1 4, 4 8 5 5", href: "tel:1-833-414-4855" }),
            ' ',
            "to activate it and you\u2019re all set."),
        React.createElement(Box, { mt: "md" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                "If you didn\u2019t get your card, you can",
                ' ',
                React.createElement(MuiLink, { text: "report it lost" },
                    React.createElement(Link, { to: "/payments/debit-card/replace-card" })),
                ".")),
        trackingUrl && (React.createElement(Box, { mt: "md" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                React.createElement(Button, { variant: "link", text: "Track your card", onClick: () => setConfirmModal({
                        showConfirmModal: true,
                        externalLink: trackingUrl,
                    }), ref: confirmationTriggerRef }))))));
};
export const DebitDelayedDescription = ({ eventModelData, setConfirmModal, confirmationTriggerRef }) => {
    const { trackingUrl } = eventModelData;
    return (React.createElement(React.Fragment, null,
        React.createElement(NeedsAttentionContainer, { tag: "div", size: "sm" },
            React.createElement(IconWrapper, { "aria-label": "Needs Attention", role: "img" },
                React.createElement(Icon, { icon: muiErrorFill, fill: "warning", size: "md", "aria-hidden": true })),
            React.createElement(TextBody, { tag: "p", size: "sm" }, "Your debit card is on the way, but it's going to arrive later than expected.")),
        trackingUrl && (React.createElement(Box, { mt: "md" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                React.createElement(Button, { variant: "link", text: "Track your card", onClick: () => setConfirmModal({
                        showConfirmModal: true,
                        externalLink: trackingUrl,
                    }), ref: confirmationTriggerRef }))))));
};
export const DebitFailedDescription = ({ eventModelData, setConfirmModal, confirmationTriggerRef }) => {
    const { trackingUrl, custCardId } = eventModelData;
    return (React.createElement(React.Fragment, null,
        React.createElement(NeedsAttentionContainer, { tag: "div", size: "sm" },
            React.createElement(IconWrapper, { "aria-label": "Needs Attention", role: "img" },
                React.createElement(Icon, { icon: muiErrorFill, fill: "warning", size: "md", "aria-hidden": true })),
            React.createElement(TextBody, { tag: "p", size: "sm" },
                "We couldn't deliver your debit card ending in ",
                custCardId,
                ". Call us at",
                ' ',
                React.createElement(MuiLink, { text: "1-877-247-2559", allytmln: "1-877-247-2559", "aria-label": "1, 8 7 7, 2 4 7, 2 5 5 9", href: "tel:1-877-247-2559" }),
                ' ',
                "so we can get you a new card.")),
        trackingUrl && (React.createElement(Box, { mt: "md" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                React.createElement(Button, { variant: "link", text: "Track your card", onClick: () => setConfirmModal({
                        showConfirmModal: true,
                        externalLink: trackingUrl,
                    }), ref: confirmationTriggerRef }))))));
};
export const DebitActivatedDescription = ({ isDebitCardControlHidden }) => {
    // if FF_hide-card-control is true, hide card control, else show card control
    return (React.createElement(React.Fragment, null,
        React.createElement(TextBody, { tag: "p", size: "sm" }, "Your card's good to go. \uD83C\uDF89"),
        !isDebitCardControlHidden && (React.createElement(Box, { mt: "md" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                "Set up",
                ' ',
                React.createElement(MuiLink, { text: "card controls", allytmln: "card controls", href: "/payments/debit-card/manage-card" }),
                ' ',
                "to set spend limits and manage your debit card push notifications.")))));
};
export const StatusModalDebitDetails = ({ event }) => {
    const { accountType, accountNumberLastFourDigits, custCardId, deliveryAddress, shipMethod, } = event.eventModelData;
    const { status } = event.statuses[0];
    const ordered = status === 'Ordered';
    const activated = status === 'Activated';
    return (React.createElement(StyledDL, null,
        React.createElement(StyledDT, null, "Account"),
        React.createElement(StyledDD, null,
            React.createElement(Private, null, getDebitCardAccountDisplay(accountType, accountNumberLastFourDigits))),
        !ordered && custCardId && (React.createElement(React.Fragment, null,
            React.createElement(StyledDT, null, "Debit card"),
            React.createElement(StyledDD, null,
                React.createElement(Private, null,
                    "Card ending in ",
                    custCardId)))),
        !activated && deliveryAddress && (React.createElement(React.Fragment, null,
            React.createElement(StyledDT, null, "Address"),
            React.createElement(StyledDD, null,
                React.createElement(Private, null, deliveryAddress)))),
        !ordered && !activated && shipMethod && (React.createElement(React.Fragment, null,
            React.createElement(StyledDT, null, "Shipping method"),
            React.createElement(StyledDD, null, shipMethod)))));
};
//# sourceMappingURL=StatusModalDebitData.js.map